import { create } from 'zustand';

import { TransferType, OrderStatus } from '@/types/enums';

import { useAccountStore } from './use-account-store';

export type NewTransfer = {
  id: string;
  size: bigint;
  orderType: TransferType;
  status: OrderStatus.NEW;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type ProcessingTransfer = {
  id: string;
  size: bigint;
  orderType: TransferType;
  status: OrderStatus.PROCESSING;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type FilledTransfer = {
  id: string;
  size: bigint;
  orderType: TransferType;
  status: OrderStatus.FILLED;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type RejectedTransfer = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.REJECTED;
  code: number;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type VerifiedTransfer = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.VERIFIED;
  size: bigint;
  chainId: number;
  postTime: number;
  requestArgs: string;
  signer: string;
  signature: string;
  signatureProof: string;
  validatorSignature: string;
  isSolanaNative: boolean;
};

export type Transfer =
  | NewTransfer
  | FilledTransfer
  | RejectedTransfer
  | VerifiedTransfer
  | ProcessingTransfer;

interface TransfersStore {
  newDeposits: Record<string, Transfer[]>;
  addNewDeposit: (transfer: Transfer, address?: string) => void;
  removeNewDeposit: (id: string, address?: string) => void;

  transfers: Record<string, Record<string, Transfer>>;
  setTransfer: (id: string, transfer: Transfer, address?: string) => void;
  getTransfers: (address?: string) => Record<string, Transfer>;
  clearTransfersStore: () => void;
}

export const useTransfersStore = create<TransfersStore>((set, get) => ({
  newDeposits: {},
  addNewDeposit: (transfer: Transfer, addressOverride) => {
    const address =
      (addressOverride ?? useAccountStore.getState().address) || '';

    set((state) => ({
      newDeposits: {
        ...state.newDeposits,
        [address]: [...(state.newDeposits[address] ?? []), transfer],
      },
    }));
  },
  removeNewDeposit: (id: string, addressOverride) => {
    const address =
      (addressOverride ?? useAccountStore.getState().address) || '';

    set((state) => ({
      newDeposits: {
        ...state.newDeposits,
        [address]: (state.newDeposits[address] ?? []).filter(
          (t) => t.id !== id,
        ),
      },
    }));
  },
  transfers: {},
  setTransfer: (id: string, transfer: Transfer, addressOverride) => {
    const address =
      (addressOverride ?? useAccountStore.getState().address) || '';

    set((state) => ({
      transfers: {
        ...state.transfers,
        [address]: { ...(state.transfers[address] ?? {}), [id]: transfer },
      },
    }));
  },
  getTransfers: (address) => {
    if (!address) return {};
    const { transfers } = get();
    if (!(address in transfers)) return {};
    return transfers[address];
  },
  clearTransfersStore: () => {
    set({
      newDeposits: {},
      transfers: {},
    });
  },
}));
